<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { yup } from '@clearbanc/validation';
import { BusinessDetails } from '@/types/business.types';
import { useVerificationCodes } from '@/composables/useVerificationCodes';
import { federalTaxIdLabel, federalTaxIdShortLabel } from '@/utils/local';
import { COUNTRY_CODES } from '@/data/supported-country-codes';
import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';

interface BusinessInformationForm {
  name?: string;
  federalTaxId?: string;
}

const store = useStore();
const props = defineProps<{ business: BusinessDetails }>();

// Use the verification codes composable
const { hasBusinessNameIssue, hasBusinessEINIssue } = useVerificationCodes();

const business = computed(() => store.getters.business);

// Computed properties for cleaner template logic
const showFirstRow = computed(
  () => hasBusinessNameIssue.value || hasBusinessEINIssue.value,
);

const computedFederalTaxIdLabel = computed(() => {
  return (
    federalTaxIdLabel(business.value.addressCountryCode) ||
    federalTaxIdShortLabel(business.value.addressCountryCode) ||
    federalTaxIdLabel(COUNTRY_CODES.US)
  );
});

// ----------
// Form Logic
// ----------

const validationSchema = yup.object({
  name: yup.businessName(),
  federalTaxId: yup.ein(business.value.addressCountryCode),
});

const { values, setValues, validate } = useForm<BusinessInformationForm>({
  validationSchema,
});

async function validateAndGetValues() {
  const { valid } = await validate();
  if (valid) {
    return {
      name: values.name,
      federalTaxId: values.federalTaxId,
    };
  }
  return undefined;
}

defineExpose({
  validateAndGetValues,
});

function populateSavedValues() {
  const { name, federalTaxId } = props.business;

  setValues({
    ...(name && { name }),
    ...(federalTaxId && { federalTaxId }),
  });
}

// -----------------
// Lifecycle Methods
// -----------------

onMounted(() => {
  populateSavedValues();
});
</script>

<template>
  <div class="c-group">
    <div class="c-group__header">
      <h3 class="c-group__header__title">Business Information</h3>
    </div>
    <div v-if="showFirstRow" class="c-form__row grid grid-cols-12">
      <div v-if="hasBusinessNameIssue" class="col-span-6 md:col-span-12">
        <InputTextGroup name="name" label="Registered Business Name" />
      </div>
      <div v-if="hasBusinessEINIssue" class="col-span-6 md:col-span-12">
        <InputTextGroup
          v-only-numbers
          name="federalTaxId"
          :label="computedFederalTaxIdLabel"
        />
      </div>
    </div>
  </div>
</template>
